import React, { useState, useEffect } from 'react';
import { Container, Button, Box, CircularProgress, Typography, Alert } from '@mui/material';
import CipherChallenge from '../components/CipherChallenge';
import { getRandomQuote, aristocratEncode, generateAristocratKey } from '../utils/cipherUtils';

const AristocratCipher = () => {
  const [challenge, setChallenge] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const generateNewChallenge = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const quote = await getRandomQuote();
      if (quote) {
        const newKey = generateAristocratKey();
        setChallenge({
          encodedMessage: aristocratEncode(quote.text, newKey),
          correctAnswer: quote.text,
          originalText: quote.text,
          hint: "Common English words like 'THE', 'AND', 'IS', and 'TO' can help you break this cipher. Also, look for single-letter words which are usually 'A' or 'I'.",
          difficulty: quote.difficulty
        });
      } else {
        throw new Error('Failed to generate challenge');
      }
    } catch (error) {
      console.error('Error generating challenge:', error);
      setError('Failed to load challenge. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    generateNewChallenge();
  }, []);

  const handleComplete = (result) => {
    if (result.success) {
      // If moveToNext is true, automatically generate a new challenge
      if (result.moveToNext) {
        generateNewChallenge();
      }
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          <Button
            variant="contained"
            color="primary"
            onClick={generateNewChallenge}
            sx={{ mt: 2 }}
          >
            Try Again
          </Button>
        </Box>
      </Container>
    );
  }

  if (!challenge) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" color="error" gutterBottom>
            No challenge available
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={generateNewChallenge}
            sx={{ mt: 2 }}
          >
            Generate New Challenge
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <CipherChallenge
        title="Aristocrat Cipher Challenge"
        description="In an Aristocrat cipher, each letter in the alphabet is replaced by another letter, but word boundaries are preserved. Look for common patterns and short words to help you decode the message!"
        encodedMessage={challenge.encodedMessage}
        correctAnswer={challenge.correctAnswer}
        originalText={challenge.originalText}
        hint={challenge.hint}
        difficulty={challenge.difficulty}
        onComplete={handleComplete}
      />
    </Container>
  );
};

export default AristocratCipher; 